import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Share2, Copy, ExternalLink, Sun, Moon, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Image } from "@/lib/types";
import { supabase } from "@/integrations/supabase/client";
import MetaHead from "@/components/MetaHead";
import { useIsMobile } from "@/hooks/use-mobile";
import ImageViewer from "@/components/ImageViewer";

const ImageView = () => {
  const { slug } = useParams<{ slug: string }>();
  const [media, setMedia] = useState<Image | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lightMode, setLightMode] = useState(false);
  const [showFullViewer, setShowFullViewer] = useState(false);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  // Use absolute URL for sharing - window.location.href ensures we get the correct deployed URL
  const shareUrl = slug ? window.location.href : '';
  const domainDirectUrl = slug ? `${window.location.origin}/img/${slug}.png` : '';

  // Function to check if an image has been viewed
  const hasViewedImage = (imageId: string): boolean => {
    const viewedImages = JSON.parse(localStorage.getItem('viewedImages') || '{}');
    return !!viewedImages[imageId];
  };

  // Function to mark an image as viewed
  const markImageAsViewed = (imageId: string): void => {
    const viewedImages = JSON.parse(localStorage.getItem('viewedImages') || '{}');
    viewedImages[imageId] = true;
    localStorage.setItem('viewedImages', JSON.stringify(viewedImages));
  };
  
  // Determine media type based on content type or URL
  const getMediaType = (): 'video' | 'gif' | 'image' | 'document' => {
    if (!media) return 'document';
    
    if (media.contentType) {
      if (media.contentType.startsWith('video/')) return 'video';
      if (media.contentType === 'image/gif') return 'gif';
      if (media.contentType.startsWith('image/')) return 'image';
      return 'document';
    }
    
    // Fallback to URL extension checking
    const url = media.url.toLowerCase();
    if (url.endsWith('.mp4') || url.endsWith('.webm') || url.endsWith('.mov')) return 'video';
    if (url.endsWith('.gif')) return 'gif';
    if (url.endsWith('.jpg') || url.endsWith('.jpeg') || url.endsWith('.png') || url.endsWith('.webp')) return 'image';
    
    return 'document';
  };
  
  const mediaType = getMediaType();
  const isVideo = mediaType === 'video';
  const isGif = mediaType === 'gif';

  useEffect(() => {
    if (!slug) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    const fetchMedia = async () => {
      try {
        // Find media with matching slug
        const { data, error } = await supabase
          .from('images')
          .select('*')
          .eq('slug', slug)
          .single();

        if (error || !data) {
          setNotFound(true);
          return;
        }

        // Get public URL for the media
        const { data: { publicUrl } } = supabase.storage
          .from('images')
          .getPublicUrl(data.storage_path);
          
        // Use stored content_type from database, or fall back to extension-based detection
        let contentType: string | undefined = data.content_type;
        
        // If no stored content type, try to determine from extension (fallback for old records)
        if (!contentType) {
          const extension = data.storage_path.split('.').pop()?.toLowerCase();
          
          if (extension) {
            if (['mp4', 'webm', 'mov'].includes(extension)) {
              contentType = `video/${extension === 'mov' ? 'quicktime' : extension}`;
            } else if (extension === 'gif') {
              contentType = 'image/gif';
            } else if (['jpg', 'jpeg', 'png', 'webp'].includes(extension)) {
              contentType = `image/${extension === 'jpg' ? 'jpeg' : extension}`;
            }
          }
        }

        // Create media object for our UI
        const mediaData: Image = {
          id: data.id,
          name: data.name,
          description: data.description || undefined,
          url: publicUrl,
          slug: data.slug,
          createdAt: new Date(data.created_at).getTime(),
          views: data.views,
          contentType,
          // Set width and height to undefined if they're not available in the database
          width: undefined,
          height: undefined
        };

        setMedia(mediaData);

        // Check if user has already viewed this media
        const alreadyViewed = hasViewedImage(data.id);
        
        // If not already viewed, update view count
        if (!alreadyViewed) {
          markImageAsViewed(data.id);
          
          // Update view count in database
          const { error: updateError } = await supabase
            .from('images')
            .update({ views: data.views + 1 })
            .eq('id', data.id);

          if (updateError) {
            console.error('Error updating view count:', updateError);
          }
        }
      } catch (error) {
        console.error('Error fetching media:', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchMedia();
  }, [slug]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard");
    } catch (err) {
      // Fallback for when clipboard API fails
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        toast.success("Link copied to clipboard");
      } catch (err) {
        toast.error("Failed to copy link");
      }
      
      document.body.removeChild(textArea);
    }
  };

  const handleCopyDirectLink = () => {
    if (media) {
      navigator.clipboard.writeText(media.url);
      toast.success("Direct media link copied to clipboard");
    }
  };

  const handleCopyDomainDirectLink = () => {
    if (domainDirectUrl) {
      navigator.clipboard.writeText(domainDirectUrl);
      toast.success("Domain direct link copied to clipboard");
    }
  };

  const handleDownload = async () => {
    console.log("Download button clicked", media);
    if (!media) return;
    
    try {
      const response = await fetch(media.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = media.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("File downloaded successfully");
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Failed to download file");
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: media?.name || "Shared media",
          text: media?.description || `Check out this ${mediaType}${media?.name ? `: ${media.name}` : ''}`,
          url: shareUrl,
        });
        toast.success("Shared successfully");
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error("Error sharing:", error);
          // Fall back to copy if sharing failed
          handleCopyLink();
        }
      }
    } else {
      // Fall back to copy if Web Share API is not available
      handleCopyLink();
    }
  };

  const toggleLightMode = () => setLightMode(!lightMode);
  
  const openFullViewer = () => setShowFullViewer(true);
  const closeFullViewer = () => setShowFullViewer(false);
  
  const renderMediaPreview = () => {
    if (!media) return null;
    
    if (isVideo) {
      return (
        <video 
          src={media.url} 
          className="w-full hover:opacity-90 transition-opacity"
          controls
          playsInline
          muted
          loop
          autoPlay={false}
          crossOrigin="anonymous"
        />
      );
    } else if (mediaType === 'image' || isGif) {
      return (
        <img 
          src={media.url} 
          alt={media.name || "Shared media"} 
          className="w-full hover:opacity-90 transition-opacity"
          crossOrigin="anonymous"
        />
      );
    } else {
      // Document file preview
      const fileExtension = media.name?.split('.').pop()?.toUpperCase() || 'FILE';
      return (
        <div className="w-full h-64 bg-black/20 flex flex-col items-center justify-center space-y-4">
          <div className="text-center">
            <div className="mb-4">
              <svg 
                className="h-16 w-16 mx-auto text-primary/70" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                />
              </svg>
            </div>
            <p className="text-lg font-medium text-white/90">{media.name}</p>
            <p className="text-sm text-white/60 mt-2">{fileExtension} File</p>
          </div>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center">
        <div className="glass-card p-8">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center">
        <div className="glass-card p-8 max-w-md text-center">
          <h1 className="text-2xl font-bold mb-4">Media not found</h1>
          <p className="mb-6">The media you're looking for doesn't exist or has been removed.</p>
          <Link to="/">
            <Button className="bg-primary hover:bg-primary/90 text-white px-6 py-2 rounded-lg">
              Go Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!media) {
    return null;
  }

  // Show fullscreen viewer
  if (showFullViewer) {
    return (
      <>
        <MetaHead
          title={media.name || `Shared ${mediaType}`}
          description={media.description || `View this shared ${mediaType}`}
          imageUrl={media.url} 
          url={shareUrl}
        />
        <ImageViewer
          imageUrl={media.url}
          name={media.name}
          description={media.description}
          onClose={closeFullViewer}
          initialLightMode={lightMode}
          contentType={media.contentType}
        />
      </>
    );
  }
  
  return (
    <>
      <MetaHead
        title={media.name || `Shared ${mediaType}`}
        description={media.description || `View this shared ${mediaType}`}
        imageUrl={media.url} 
        url={shareUrl}
      />
      
      <div className={`min-h-screen ${lightMode ? 'bg-gray-100' : 'gradient-bg'} ${isMobile ? 'p-2' : 'p-4 md:p-8'} transition-colors duration-300`}>
        <div className="container mx-auto max-w-5xl">
          <div className={`flex items-center justify-between ${isMobile ? 'mb-3' : 'mb-6'}`}>
            <button 
              onClick={() => navigate(-1)}
              className={`flex items-center gap-2 ${lightMode ? 'text-gray-700' : 'text-white/80 hover:text-white'} ${isMobile ? 'text-sm' : ''}`}
            >
              <ArrowLeft size={isMobile ? 18 : 20} />
              <span>Back</span>
            </button>
            <div className={`flex ${isMobile ? 'gap-1' : 'gap-2'}`}>
              <Button 
                onClick={toggleLightMode}
                className={`flex items-center justify-center ${isMobile ? 'w-8 h-8' : 'w-10 h-10'} p-0 rounded-full ${lightMode ? 'bg-gray-200 text-gray-700' : 'bg-black/20 text-white'}`}
                title={`Switch to ${lightMode ? 'dark' : 'light'} mode`}
              >
                {lightMode ? <Moon size={isMobile ? 14 : 18} /> : <Sun size={isMobile ? 14 : 18} />}
              </Button>
              <Button 
                onClick={handleDownload}
                className={`flex items-center gap-2 ${lightMode ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-green-500 hover:bg-green-600 text-white'} ${isMobile ? 'px-2 py-1 text-xs' : 'px-4 py-2'} rounded-lg`}
                title="Download file"
              >
                <Download size={isMobile ? 14 : 18} />
                <span className="hidden sm:inline">Download</span>
              </Button>
              <Button 
                onClick={handleShare}
                className={`flex items-center gap-2 ${lightMode ? 'bg-primary/90 text-white' : 'bg-primary hover:bg-primary/90'} ${isMobile ? 'px-2 py-1 text-xs' : 'px-4 py-2'} rounded-lg`}
                title="Share media"
              >
                <Share2 size={isMobile ? 14 : 18} />
                <span className="hidden sm:inline">Share</span>
              </Button>
              <Button 
                onClick={handleCopyLink}
                className={`flex items-center gap-2 ${lightMode ? 'bg-gray-200 text-gray-700' : 'bg-primary/20 hover:bg-primary/30'} ${isMobile ? 'px-2 py-1 text-xs' : 'px-4 py-2'} rounded-lg`}
                title="Copy link"
              >
                <Copy size={isMobile ? 14 : 18} />
                <span className="hidden sm:inline">Copy Link</span>
              </Button>
            </div>
          </div>
          
          <Card className={`overflow-hidden ${lightMode ? 'bg-white shadow-lg' : 'glass-card'}`}>
            <div 
              className="relative cursor-pointer bg-black/10" 
              onClick={openFullViewer}
            >
              <div className="w-full">
                {renderMediaPreview()}
              </div>
              {!isMobile && !isVideo && (
                <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 bg-black/30 transition-opacity">
                  <span className="bg-black/60 text-white px-4 py-2 rounded-lg">Click to expand</span>
                </div>
              )}
            </div>
            
            <CardContent className={`${isMobile ? 'p-3' : 'p-4'} ${lightMode ? 'bg-white text-gray-800' : ''}`}>
              <h1 className={`${isMobile ? 'text-lg' : 'text-xl'} font-medium ${isMobile ? 'mb-1' : 'mb-2'}`}>{media.name}</h1>
              {media.description && (
                <p className={`${lightMode ? 'text-gray-600' : 'text-white/70'} ${isMobile ? 'mb-2 text-sm' : 'mb-4'}`}>{media.description}</p>
              )}
              <div className={`${isMobile ? 'text-xs' : 'text-sm'} ${lightMode ? 'text-gray-500' : 'text-white/50'} ${isMobile ? 'mb-3' : 'mb-4'}`}>
                {media.views} {media.views === 1 ? 'view' : 'views'} · Uploaded {new Date(media.createdAt).toLocaleDateString()}
                {isVideo && <span> · Video</span>}
                {isGif && <span> · GIF</span>}
              </div>
              
              {/* Share links section - more compact on mobile */}
              <div className={`${isMobile ? 'mt-3 p-3' : 'mt-6 p-4'} rounded-lg ${lightMode ? 'bg-gray-100' : 'bg-black/20'}`}>
                <h3 className={`${isMobile ? 'text-sm' : 'text-md'} font-medium ${isMobile ? 'mb-2' : 'mb-3'}`}>Media Links</h3>
                <div className={`space-y-${isMobile ? '2' : '3'}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <span className={`${isMobile ? 'text-xs' : 'text-sm'} ${lightMode ? 'text-gray-600' : 'text-white/70'} whitespace-nowrap`}>Share URL:</span>
                    <div className="flex-1 flex gap-2 items-center">
                      <input 
                        type="text" 
                        readOnly 
                        value={shareUrl} 
                        className={`flex-1 ${lightMode ? 'bg-gray-200 text-gray-800' : 'bg-black/20 text-white/90'} ${isMobile ? 'p-1.5 text-xs' : 'p-2 text-sm'} rounded overflow-hidden text-ellipsis`}
                      />
                      <Button 
                        onClick={handleCopyLink} 
                        className={`shrink-0 ${isMobile ? 'h-6 w-6' : 'h-8 w-8'} p-0 flex items-center justify-center rounded-full ${lightMode ? 'hover:bg-gray-300' : 'hover:bg-black/20'}`}
                        title="Copy share link"
                      >
                        <Copy size={isMobile ? 12 : 16} />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <span className={`${isMobile ? 'text-xs' : 'text-sm'} ${lightMode ? 'text-gray-600' : 'text-white/70'} whitespace-nowrap`}>Domain URL:</span>
                    <div className="flex-1 flex gap-2 items-center">
                      <input 
                        type="text" 
                        readOnly 
                        value={domainDirectUrl} 
                        className={`flex-1 ${lightMode ? 'bg-gray-200 text-gray-800' : 'bg-black/20 text-white/90'} ${isMobile ? 'p-1.5 text-xs' : 'p-2 text-sm'} rounded overflow-hidden text-ellipsis`}
                      />
                      <Button 
                        onClick={handleCopyDomainDirectLink} 
                        className={`shrink-0 ${isMobile ? 'h-6 w-6' : 'h-8 w-8'} p-0 flex items-center justify-center rounded-full ${lightMode ? 'hover:bg-gray-300' : 'hover:bg-black/20'}`}
                        title="Copy domain direct link"
                      >
                        <Copy size={isMobile ? 12 : 16} />
                      </Button>
                      {!isMobile && (
                        <a 
                          href={domainDirectUrl} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className={`shrink-0 h-8 w-8 p-0 flex items-center justify-center rounded-full ${lightMode ? 'hover:bg-gray-300' : 'hover:bg-black/20'}`}
                          title="Open domain direct link"
                        >
                          <ExternalLink size={16} />
                        </a>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <span className={`${isMobile ? 'text-xs' : 'text-sm'} ${lightMode ? 'text-gray-600' : 'text-white/70'} whitespace-nowrap`}>Direct file:</span>
                    <div className="flex-1 flex gap-2 items-center">
                      <input 
                        type="text" 
                        readOnly 
                        value={media.url} 
                        className={`flex-1 ${lightMode ? 'bg-gray-200 text-gray-800' : 'bg-black/20 text-white/90'} ${isMobile ? 'p-1.5 text-xs' : 'p-2 text-sm'} rounded overflow-hidden text-ellipsis`}
                      />
                      <Button 
                        onClick={handleCopyDirectLink} 
                        className={`shrink-0 ${isMobile ? 'h-6 w-6' : 'h-8 w-8'} p-0 flex items-center justify-center rounded-full ${lightMode ? 'hover:bg-gray-300' : 'hover:bg-black/20'}`}
                        title="Copy direct link"
                      >
                        <Copy size={isMobile ? 12 : 16} />
                      </Button>
                      {!isMobile && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              className={`shrink-0 h-8 w-8 p-0 flex items-center justify-center rounded-full ${lightMode ? 'hover:bg-gray-300' : 'hover:bg-black/20'}`}
                              title="Preview direct media"
                            >
                              <ExternalLink size={16} />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 max-w-[300px]">
                            <div className="p-2">
                              {isVideo ? (
                                <video 
                                  src={media.url} 
                                  className="max-w-full rounded"
                                  controls
                                  muted
                                  autoPlay={false}
                                  loop
                                  playsInline
                                  crossOrigin="anonymous"
                                  style={{ maxHeight: '200px' }}
                                />
                              ) : (
                                <img 
                                  src={media.url} 
                                  alt="Direct preview" 
                                  className="max-w-full rounded"
                                  crossOrigin="anonymous"
                                />
                              )}
                              <div className="p-2 text-center">
                                <a 
                                  href={media.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-500 hover:underline"
                                >
                                  Open in new tab
                                </a>
                              </div>
                            </div>
                          </PopoverContent>
                        </Popover>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default ImageView;
